import { collection, doc, getDoc, getDocs, updateDoc } from "firebase/firestore";

import { firebaseDb, isFirebaseConfigured, tryEnsureFirebaseAuth } from "data/firebase";

const BUSINESS_APPLICATIONS = "businessApplications";
const DEALS = "deals";
const DEAL_APPLICATION_KIND = "dealApplication";
const ADS = "ads";
const AD_APPLICATION_KIND = "adApplication";
const TRAVEL_TOURS = "travelTours";
const TOUR_APPLICATION_KIND = "tourApplication";
const REGION_APPLICATIONS = "regionApplications";
const REGION_APPLICATION_KIND = "regionApplication";

function requireDb() {
  if (!isFirebaseConfigured || !firebaseDb) {
    throw new Error("Firebase yapılandırılmamış.");
  }
  return firebaseDb;
}

function nowIso() {
  return new Date().toISOString();
}

function asStatus(value) {
  return value === "approved" || value === "rejected" ? value : "pending";
}

function str(value) {
  return value === undefined || value === null ? "" : String(value);
}

function asStringList(value) {
  if (!Array.isArray(value)) return [];
  return value.map((item) => str(item).trim()).filter(Boolean);
}

export function isDealApplicationDoc(raw = {}) {
  return (
    raw.applicationKind === DEAL_APPLICATION_KIND ||
    Boolean(raw.businessName && raw.contactName && (raw.approvalStatus || raw.status === "pending"))
  );
}

function asBusinessApplication(id, raw = {}) {
  return {
    id,
    kind: "business",
    businessName: str(raw.businessName),
    category: str(raw.category) || "hotel",
    provinceId: str(raw.provinceId),
    district: str(raw.district),
    address: str(raw.address),
    contactName: str(raw.contactName),
    phone: str(raw.phone),
    email: str(raw.email),
    website: str(raw.website),
    taxNumber: str(raw.taxNumber),
    description: str(raw.description),
    imageUrl: str(raw.imageUrl),
    attachmentUrls: asStringList(raw.attachmentUrls),
    status: asStatus(raw.status),
    adminNote: str(raw.adminNote),
    businessId: str(raw.businessId),
    userId: str(raw.userId),
    reviewedAt: str(raw.reviewedAt),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  };
}

function asDealApplication(id, raw = {}) {
  return {
    id,
    kind: "deal",
    businessName: str(raw.businessName),
    title: str(raw.title),
    description: str(raw.description),
    provinceId: str(raw.provinceId),
    theme: str(raw.theme),
    priceText: str(raw.priceText),
    startDate: str(raw.startDate),
    endDate: str(raw.endDate),
    contactCta: str(raw.contactCta),
    contactName: str(raw.contactName),
    phone: str(raw.phone),
    email: str(raw.email),
    status: asStatus(raw.approvalStatus || raw.status),
    adminNote: str(raw.adminNote),
    reviewedAt: str(raw.reviewedAt),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  };
}

export function isAdApplicationDoc(raw = {}) {
  return raw.applicationKind === AD_APPLICATION_KIND;
}

export function isTourApplicationDoc(raw = {}) {
  return raw.applicationKind === TOUR_APPLICATION_KIND;
}

export function isRegionApplicationDoc(raw = {}) {
  return raw.applicationKind === REGION_APPLICATION_KIND || Boolean(raw.organizationName && raw.title);
}

function asAdApplication(id, raw = {}) {
  return {
    id,
    kind: "ad",
    businessName: str(raw.businessName),
    title: str(raw.title),
    subtitle: str(raw.subtitle),
    description: str(raw.description),
    provinceId: str(raw.provinceId),
    imageUrl: str(raw.imageUrl),
    linkUrl: str(raw.linkUrl),
    placement: str(raw.placement) || "all",
    contactName: str(raw.contactName),
    phone: str(raw.phone),
    email: str(raw.email),
    source: str(raw.source),
    status: asStatus(raw.approvalStatus || raw.status),
    adminNote: str(raw.adminNote),
    reviewedAt: str(raw.reviewedAt),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  };
}

function asTourApplication(id, raw = {}) {
  return {
    id,
    kind: "tour",
    businessName: str(raw.agencyName || raw.businessName || raw.operatorName),
    title: str(raw.title),
    description: str(raw.description || raw.details),
    provinceId: str(raw.provinceId),
    route: str(raw.route),
    days: Number(raw.days) || 0,
    nights: Number(raw.nights) || 0,
    duration: str(raw.duration),
    priceTry: Number(raw.priceTry) || 0,
    priceText: str(raw.priceText),
    transport: str(raw.transport),
    accommodation: str(raw.accommodation),
    dateRange: str(raw.dateRange),
    categoryTag: str(raw.categoryTag),
    highlights: asStringList(raw.highlights),
    included: asStringList(raw.included),
    excluded: asStringList(raw.excluded),
    imageUrl: str(raw.imageUrl),
    brochureUrl: str(raw.brochureUrl),
    contactName: str(raw.contactName),
    phone: str(raw.phone),
    email: str(raw.email),
    source: str(raw.source),
    kadekonDonationAccepted: Boolean(raw.kadekonDonationAccepted),
    status: asStatus(raw.approvalStatus || raw.status),
    adminNote: str(raw.adminNote),
    reviewedAt: str(raw.reviewedAt),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  };
}

function asRegionApplication(id, raw = {}) {
  return {
    id,
    kind: "region",
    businessName: str(raw.organizationName),
    title: str(raw.title),
    description: str(raw.summary),
    provinceId: str(raw.provinceId),
    district: str(raw.district),
    highlights: asStringList(raw.highlights),
    imageUrl: str(raw.imageUrl),
    contactName: str(raw.contactName),
    phone: str(raw.phone),
    email: str(raw.email),
    source: str(raw.source),
    kadekonDonationAccepted: Boolean(raw.kadekonDonationAccepted),
    status: asStatus(raw.approvalStatus || raw.status),
    adminNote: str(raw.adminNote),
    reviewedAt: str(raw.reviewedAt),
    createdAt: str(raw.createdAt),
    updatedAt: str(raw.updatedAt),
  };
}

function byNewest(left, right) {
  return right.createdAt.localeCompare(left.createdAt);
}

export async function listBusinessApplicationsFromFirestore() {
  const db = requireDb();
  await tryEnsureFirebaseAuth();
  const snap = await getDocs(collection(db, BUSINESS_APPLICATIONS));
  return snap.docs.map((item) => asBusinessApplication(item.id, item.data())).sort(byNewest);
}

export async function listDealApplicationsFromFirestore() {
  const db = requireDb();
  await tryEnsureFirebaseAuth();
  const snap = await getDocs(collection(db, DEALS));
  return snap.docs
    .filter((item) => isDealApplicationDoc(item.data()))
    .map((item) => asDealApplication(item.id, item.data()))
    .sort(byNewest);
}

export async function listAdApplicationsFromFirestore() {
  const db = requireDb();
  await tryEnsureFirebaseAuth();
  const snap = await getDocs(collection(db, ADS));
  return snap.docs
    .filter((item) => isAdApplicationDoc(item.data()))
    .map((item) => asAdApplication(item.id, item.data()))
    .sort(byNewest);
}

export async function listTourApplicationsFromFirestore() {
  const db = requireDb();
  await tryEnsureFirebaseAuth();
  const snap = await getDocs(collection(db, TRAVEL_TOURS));
  return snap.docs
    .filter((item) => isTourApplicationDoc(item.data()))
    .map((item) => asTourApplication(item.id, item.data()))
    .sort(byNewest);
}

export async function listRegionApplicationsFromFirestore() {
  const db = requireDb();
  await tryEnsureFirebaseAuth();
  const snap = await getDocs(collection(db, REGION_APPLICATIONS));
  return snap.docs
    .filter((item) => isRegionApplicationDoc(item.data()))
    .map((item) => asRegionApplication(item.id, item.data()))
    .sort(byNewest);
}

async function loadPending(collectionName, id, mapper) {
  const db = requireDb();
  await tryEnsureFirebaseAuth();
  const ref = doc(db, collectionName, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error("Başvuru kaydı bulunamadı.");
  const application = mapper(snap.id, snap.data());
  if (application.status !== "pending") throw new Error("Bu başvuru zaten incelenmiş.");
  return { ref, application };
}

function reviewPatch(status, note) {
  const now = nowIso();
  const patch = { status, updatedAt: now, reviewedAt: now };
  if (note && note.trim()) patch.adminNote = note.trim();
  return patch;
}

export async function reviewBusinessApplicationInFirestore(id, status, note) {
  const { ref, application } = await loadPending(BUSINESS_APPLICATIONS, id, asBusinessApplication);
  const patch = reviewPatch(status, note);
  if (status === "approved") {
    patch.businessId = application.businessId || `biz_${application.id.replace(/^bapp_/, "")}`;
  }
  await updateDoc(ref, patch);
  return { ...application, ...patch };
}

export async function reviewDealApplicationInFirestore(id, status, note) {
  const { ref, application } = await loadPending(DEALS, id, asDealApplication);
  const patch = { ...reviewPatch(status, note), approvalStatus: status };
  if (status === "approved") patch.dealId = application.id;
  await updateDoc(ref, patch);
  return { ...application, ...patch };
}

export async function reviewAdApplicationInFirestore(id, status, note) {
  const { ref, application } = await loadPending(ADS, id, asAdApplication);
  const patch = { ...reviewPatch(status, note), approvalStatus: status, isActive: status === "approved" };
  await updateDoc(ref, patch);
  return { ...application, ...patch };
}

export async function reviewTourApplicationInFirestore(id, status, note) {
  const { ref, application } = await loadPending(TRAVEL_TOURS, id, asTourApplication);
  const patch = {
    ...reviewPatch(status, note),
    approvalStatus: status,
    isActive: status === "approved",
  };
  await updateDoc(ref, patch);
  return { ...application, ...patch };
}

export async function reviewRegionApplicationInFirestore(id, status, note) {
  const { ref, application } = await loadPending(REGION_APPLICATIONS, id, asRegionApplication);
  const patch = { ...reviewPatch(status, note), approvalStatus: status };
  await updateDoc(ref, patch);
  return { ...application, ...patch };
}
